import classNames from "classnames";
import classes from "../../../../substeps/ShaftMachiningSubstep/components/SegmentCommonStyles.module.less";
import { Card } from "~/Configurators/Linear/components/Card";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import { SimpleCsticId } from "~/Configurators/Linear/models";
import {
  axialThread,
  SHAFT_MACHINING_LEFT,
  SHAFT_MACHINING_RIGHT,
} from "~/Configurators/Shaft/metric/metricCsticConstants";
import { SubstepType } from "~/Configurators/Linear/dtos";
import { SimpleCsticNumberField } from "~/Configurators/Shaft/components/SimpleCsticNumberField";
import { Button } from "~/common/components/Button/Button";
import LanguageAwareExternalContent from "~/Configurators/Linear/components/LanguageAwareExternalContent";
import { selectCstic, selectCsticExpanded } from "~/Configurators/Linear/redux/selectCstic";
import { updateCsticValues } from "~/Configurators/Linear/redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { SimpleCsticTileField } from "~/Configurators/Shaft/components/SimpleCsticTileField";
import { LinearTileV2 } from "~/Configurators/Shaft/substeps/LinearTileV2";
import {
  showAxialThreadSection,
  validateAxialThread,
  type ValidationMode,
} from "~/Configurators/Shaft/shaftConfiguratorHelper";
import type { RootState } from "~/redux/store";
import {
  ErrorMessage,
  selectIsMachiningExtraSubstepInvalid,
} from "~/Configurators/Shaft/metric/components/MetricShaftMachiningExtraSubstep/MetricShaftMachiningExtraSubstepHelper";
import { selectMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import { selectConflictedCstics } from "~/Configurators/Linear/redux/selectors";
import LinearInfo from "~/Configurators/Linear/components/LinearInfo";
import { KmatType } from "~/Configurators/Linear/kmatType";

interface Props {
  side: "left" | "right";
}

const CONFIG = {
  left: {
    image: "configurator.shaft.step.3.details.axialInternalThread.image.left",
    size: SimpleCsticId(axialThread.left.size),
    depth: SimpleCsticId(axialThread.left.depth),
    machining: SimpleCsticId(SHAFT_MACHINING_LEFT),
    otherSide: "right" as const,
    buttonLabel: "configurator.shaft.step.3.details.axialInternalThread.copyToRight",
    info: "configurator.shaft.step.2.substep.3.tile.003.infoPopup.left",
  },
  right: {
    image: "configurator.shaft.step.3.details.axialInternalThread.image.right",
    size: SimpleCsticId(axialThread.right.size),
    depth: SimpleCsticId(axialThread.right.depth),
    machining: SimpleCsticId(SHAFT_MACHINING_RIGHT),
    otherSide: "left" as const,
    info: "configurator.shaft.step.2.substep.3.tile.003.infoPopup.right",
    // Hide copy to left button
    // buttonLabel: "configurator.shaft.step.3.details.axialInternalThread.copyToLeft",
  },
};

export const MetricShaftAxialThreadSegment = ({ side }: Props) => {
  const t = useXmlConfiguratorTranslations().t!;
  const config = CONFIG[side];
  const otherSideConfig = CONFIG[config.otherSide];

  const sizeValue = useSelector(selectCstic(config.size))?.value ?? "";
  const dispatch = useDispatch();

  const otherSideMachining = useSelector(selectCstic(otherSideConfig.machining))?.value ?? "";
  const showButton = showAxialThreadSection(otherSideMachining);

  const setOtherSide = () => {
    dispatch(
      updateCsticValues(
        [
          {
            ...otherSideConfig.size,
            value: sizeValue,
          },
        ],
        SubstepType.ShaftMachiningExtra,
      ),
    );
  };

  const errors = useSelector(selectErrors({ depth: config.depth.key, size: config.size.key }));
  const hasErrors = errors.length > 0;

  const errorVariant = errors.find((error) => error.type === "required") ? "select" : "input";

  return (
    <div className={classNames(classes.root)}>
      <div className={classes.titleSection}>
        <LanguageAwareExternalContent
          content={"configurator.shaft.step.3.details.axialInternalThread.title"}
          loadedClassname={classNames("scha__lc__external-label", classes.segmentTitle)}
        />
        <LinearInfo info={config.info} />
      </div>
      <Card className={classes.imageSection}>
        <img className={classes.image} src={t(config.image)} alt="" />
      </Card>
      <div className={classes.inputSection}>
        {hasErrors && <ErrorMessage variant={errorVariant} />}
        <SimpleCsticTileField
          className={classes.fullWidth}
          csticId={config.size}
          substepType={SubstepType.ShaftMachiningExtra}
          tilesPerRow={4}
          renderTile={({ onClick, disabled, selected, value }) => (
            <LinearTileV2.Base onClick={onClick} selected={selected} disabled={disabled} error={selected && hasErrors}>
              <LinearTileV2.CheckedIndicator />
              <p className="m-0">{value.value}</p>
            </LinearTileV2.Base>
          )}
        />
        <SimpleCsticNumberField
          label={"configurator.shaft.step.3.details.axialInternalThread.infoPopup.right"}
          csticId={config.depth}
          substepType={SubstepType.ShaftMachiningExtra}
          decimalScale={2}
          disabled={true}
        />
        {"buttonLabel" in config && showButton && (
          <Button className={classes.fullWidth} onClick={setOtherSide} color={"secondary"} size={"medium"}>
            <LanguageAwareExternalContent content={config.buttonLabel} loadedClassname={"scha__lc__external-label"} />
          </Button>
        )}
      </div>
    </div>
  );
};

// Get the 2nd param of isAxialThreadFullyConfigured as a type
type Config = Parameters<typeof validateAxialThread>[0];
const selectErrors = (config: Config) => (state: RootState) => {
  const isSubstepInvalid = selectIsMachiningExtraSubstepInvalid(state);
  const { isDirty } = selectCsticExpanded(
    SimpleCsticId(config.size, KmatType.Main),
    SubstepType.ShaftMachiningExtra,
  )(state);

  const mode: ValidationMode = isDirty || isSubstepInvalid ? "full" : "feedback";
  const kmat = selectMainKmat(state);
  const conflictedCstics = selectConflictedCstics(state);
  return validateAxialThread(config, { kmat, conflictedCstics, mode });
};
